import type { GameData } from './types';
import { GameStateManager } from './GameState';
import { MovementSystem } from '../systems/MovementSystem';
import { ResourceSystem } from '../systems/ResourceSystem';
import { ResearchSystem } from '../systems/ResearchSystem';
import { OrderSystem } from '../systems/OrderSystem';
import { SanitySystem } from '../systems/SanitySystem';
import { RealityBreachSystem } from '../systems/RealityBreachSystem';
import { getSpeedMultiplier } from '../utils/GameUtils';

export class Game {
  private gameState: GameStateManager;
  private gameData: GameData;
  private movementSystem: MovementSystem;
  private resourceSystem: ResourceSystem;
  private researchSystem: ResearchSystem;
  private orderSystem: OrderSystem;
  private sanitySystem: SanitySystem;
  private realityBreachSystem: RealityBreachSystem;
  private animationFrameId: number | null = null;
  private lastTickTime: number = 0;
  private tickInterval: number = 1000; // 1 second = 1 tick at 1x speed

  constructor(gameData: GameData, gameState: GameStateManager) {
    this.gameData = gameData;
    this.gameState = gameState;
    this.movementSystem = new MovementSystem(gameState, gameData);
    this.resourceSystem = new ResourceSystem(gameState, gameData);
    this.researchSystem = new ResearchSystem(gameState, gameData, this.resourceSystem);
    this.orderSystem = new OrderSystem(gameState, gameData, this.movementSystem, this.researchSystem, this.resourceSystem);
    this.sanitySystem = new SanitySystem(gameState, gameData, this.movementSystem);
    this.realityBreachSystem = new RealityBreachSystem(gameState, gameData);
  }

  start(): void {
    this.lastTickTime = performance.now();
    this.gameLoop();
  }

  stop(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  private gameLoop(): void {
    const now = performance.now();
    const state = this.gameState.getState();

    if (!state.isPaused) {
      const speedMultiplier = getSpeedMultiplier(state.gameSpeed);
      const elapsed = now - this.lastTickTime;
      const tickTime = this.tickInterval / speedMultiplier;

      if (elapsed >= tickTime) {
        this.processTick();
        this.lastTickTime = now;
      }
    }

    this.animationFrameId = requestAnimationFrame(() => this.gameLoop());
  }


  private processTick(): void {
    const state = this.gameState.getState();

    // Check if game has ended
    if (this.gameState.getGameEndState() !== null) {
      return; // Stop processing if game has ended
    }

    // Update budget drain
    this.resourceSystem.updateBudgetDrain();

    // Check for wealth victory after budget update
    const budget = this.gameState.getBudget();
    const wealthThreshold = this.gameData.gameEndData.wealth_threshold;
    if (budget >= wealthThreshold) {
      this.gameState.setGameEndState('victory', 'wealth');
      return;
    }

    // Update breach timers
    this.orderSystem.getBreachSystem().updateBreachTimers();

    // Update researchers
    for (const researcher of state.researchers) {
      // Update movement - also check for movementPath to continue multi-step movement
      const hasMovementPath = researcher.movementPath;
      const hasMoreSteps = hasMovementPath && hasMovementPath.length > 0;
      
      if (researcher.targetLocation !== null || hasMoreSteps) {
        if (researcher.location) {
          this.movementSystem.updateMovement(researcher);
        }
      }

      // Update action timers
      if (researcher.currentAction !== null) {
        if (researcher.currentAction === 'Synthetize' || researcher.currentAction === 'Rest') {
          // Increment timer for ongoing actions
          researcher.actionTimer++;
        } else if (researcher.actionTimer > 0) {
          // Decrement timer for timed actions
          researcher.actionTimer--;
          
          if (researcher.actionTimer === 0) {
            this.orderSystem.completeAction(researcher);
          }
        }
      }

      // Update ongoing actions (Synthetize, Rest)
      const actionResult = this.resourceSystem.updateOngoingActions(researcher);
      if (actionResult.shouldCancel) {
        this.orderSystem.cancelOngoingAction(researcher);
      } else if (actionResult.shouldComplete) {
        if (researcher.currentAction === 'Synthetize') {
          // Synthetize reached max chemicals - complete the action
          this.orderSystem.completeSynthetize(researcher);
        } else if (researcher.currentAction === 'Rest') {
          // Rest reached max sanity (and no insane researchers) - complete the action
          this.orderSystem.completeRest(researcher);
        }
      }
    }

    // Update sanity system (Memetic Contagion and death handling)
    this.sanitySystem.updateSanity();

    // Check for reality breaches
    this.realityBreachSystem.checkForRealityBreach();

    // Check for defeat by losing all researchers (after all systems have updated)
    const remainingResearchers = this.gameState.getResearchers();
    if (remainingResearchers.length === 0) {
      this.gameState.setGameEndState('defeat', 'researchers');
      return;
    }

    this.gameState.incrementTick();
  }

  getGameState(): GameStateManager {
    return this.gameState;
  }

  getOrderSystem(): OrderSystem {
    return this.orderSystem;
  }

  getRealityBreachSystem(): RealityBreachSystem {
    return this.realityBreachSystem;
  }
}


